import React, { useState, useEffect } from 'react';
import './Pages.css';
import './PartnerDownloads.css';

interface DownloadAsset {
  name: string;
  filename: string;
  description: string;
  size: string;
  format: string;
  category: 'logo' | 'brand' | 'marketing';
}

// Define available assets
const availableAssets: DownloadAsset[] = [
  {
    name: 'MINTkitchen Logo (Black)',
    filename: 'mintKitchenLogo_black.png',
    description: '',
    size: '1280 × 600 px',
    format: 'PNG',
    category: 'logo'
  },
  {
    name: 'MINTkitchen Logo (White)',
    filename: 'mintKitchenLogo_white.png',
    description: '',
    size: '1280 × 600 px',
    format: 'PNG',
    category: 'logo'
  },
  {
    name: 'MINTkitchen Logo (Color)',
    filename: 'mintKitchenLogoColor.png',
    description: '',
    size: '1280 × 600 px',
    format: 'PNG',
    category: 'logo'
  },
  {
    name: 'MINTkitchen Logo (Vector)',
    filename: 'mintKitchenLogoColor.svg',
    description: '',
    size: 'Vector (SVG)',
    format: 'SVG',
    category: 'logo'
  }
];

const PartnerDownloads: React.FC = () => {
  const [assets, setAssets] = useState<DownloadAsset[]>([]);

  useEffect(() => {
    setAssets(availableAssets);
  }, []);

  const handleDownload = (asset: DownloadAsset) => {
    const link = document.createElement('a');
    link.href = `/assets/partnerdownloads/${asset.filename}`;
    link.download = asset.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'logo':
        return '🎨';
      case 'brand':
        return '🏢';
      case 'marketing':
        return '📢';
      default:
        return '📁';
    }
  };

  const isImageFormat = (format: string) => {
    const imageFormats = ['png', 'jpg', 'jpeg', 'svg', 'gif', 'webp'];
    return imageFormats.includes(format.toLowerCase());
  };

  const isLightColoredAsset = (filename: string) => {
    // Check if the asset is likely to have light colors (white logo, etc.)
    const lightAssetKeywords = ['white', 'light', 'bright'];
    return lightAssetKeywords.some(keyword => 
      filename.toLowerCase().includes(keyword)
    );
  };


  return (
    <div className="page partner-downloads-page">
      <style>{`
        .partner-downloads-page .page-header { background: #008F99; color: #ffffff; }
        .partner-downloads-page .page-title { color: #ffffff; }
        .partner-downloads-page .content-section { background: #ffffff; }
      `}</style>
      
      <section className="page-header">
        <div className="container">
          <div className="page-title">Partner Downloads</div>
        </div>
      </section>
      
      <section className="content-section">
        <div className="container">
          {/* Assets Grid */}
          <div className="assets-grid">
            {assets.length > 0 ? (
              assets.map((asset, index) => (
              <div key={index} className="asset-card">
                <span className="format-badge">
                  {asset.format}
                </span>
                
                {isImageFormat(asset.format) ? (
                  <div className={`asset-preview ${isLightColoredAsset(asset.filename) ? 'light-asset-bg' : ''}`}>
                    <img 
                      src={`/assets/partnerdownloads/${asset.filename}`}
                      alt={asset.name}
                      className="preview-image"
                    />
                  </div>
                ) : (
                  <div className="asset-header">
                    <div className="asset-icon">
                      {getCategoryIcon(asset.category)}
                    </div>
                    <div className="asset-info">
                      <h3 className="asset-name">{asset.name}</h3>
                      {asset.description && <p className="asset-description">{asset.description}</p>}
                    </div>
                  </div>
                )}
                
                {isImageFormat(asset.format) && (
                  <div className="asset-info-below">
                    <h3 className="asset-name">{asset.name}</h3>
                    {asset.description && <p className="asset-description">{asset.description}</p>}
                  </div>
                )}
                
                <div className="asset-details">
                  <div className="asset-meta">
                    <span className="size-info">{asset.size}</span>
                  </div>
                  
                  <button
                    onClick={() => handleDownload(asset)}
                    className="download-button"
                  >
                    Download
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="no-assets">
              <div className="no-assets-icon">📁</div>
              <h3>No assets found</h3>
              <p>Try adjusting your search or filter criteria</p>
            </div>
          )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnerDownloads;
